import type { Texture } from 'pixi.js';
import { PixelBuf, RAMPS, bolt, crescent, fieldTexture, hex, rand, ring, type BoltShape, type FxFrames } from './pixelfx';

/**
 * 스킬마다 다른 겉모습.
 *
 * pixelfx.ts 의 기본 도형(반달·고리·탄 조각)만으로는 같은 속성끼리 전부 닮아 보였다.
 * 지진 파동과 암석 파편이 같은 돌멩이, 감전과 전류가 같은 노란 선이었다.
 * 여기서는 스킬 하나하나에 "그 기술만의 실루엣"을 준다.
 *
 *   베기   — 반달 / 얼음 가시 부채 / 채찍 / 세 줄 칼바람 / 망치 충격 / 창 찌르기 / 번개 돌진 / 잿빛 대검 / 삼색 회오리
 *   탄     — 불티 / 빙편 / 전기 침 / 벼락 / 공기 방울 / 돌조각 / 불덩이 / 물마루 / 바늘 기류 / 땅 물결 / 룬 / 여제의 창
 *   파편   — 불꽃 뭉치 / 바람 초승달 / 나뭇잎 / 강철 낫
 *   궤도   — 도깨비불 / 정령구
 *   오라   — 흑요석 조각 / 발구름 / 세포막
 *   장판   — 눈발 / 안개 / 돌기둥 / 눈꽃 결정 / 인장
 *   폭발   — 고리 / 불 폭발 / 공기 충격파 / 전기 별 / 발톱 자국
 *
 * 규칙은 pixelfx 와 같다. 정수 격자, 5단 램프, 최근접 확대.
 */

type RGB = [number, number, number];

/** 속성 램프만으로는 같은 속성끼리 구분이 안 돼서 둔 스킬 전용 램프 (밝음 -> 어두움) */
export const FX_RAMPS = {
  /** 주황 불길 — 적소하의 진홍(복마검)과 불 마법을 가른다 */
  flame: ['#fff7e0', '#ffd36b', '#ff8a1f', '#c24a0c', '#4f1604'],
  /** 잿빛 대검 */
  ash: ['#f4efe9', '#c9c2bb', '#8f8680', '#57504c', '#262220'],
  /** 흑요석 — 검은 유리에 보랏빛 윤 */
  obsidian: ['#f3ecff', '#b9a2f0', '#7a62b8', '#44336e', '#1c1430'],
  /** 정전기 — 감전은 전류와 달리 희고 보랏빛으로 튄다 */
  violet: ['#ffffff', '#e2d4ff', '#a98bff', '#6a4bc9', '#2c1d5e'],
  leaf: ['#f1ffe8', '#bff5a0', '#6fd36a', '#3a8f4a', '#173b22'],
  fog: ['#f0f6fa', '#c9d6df', '#93a5b3', '#5d6f7d', '#2c3842'],
  gold: ['#fffbe8', '#ffe39a', '#d9a94a', '#8a6420', '#3b2a0c'],
  pink: ['#fff0f3', '#ffc2cf', '#ff8fa3', '#c9405c', '#5a1022'],
  /** 여제 — 금빛 창에 보랏빛 전하 */
  royal: ['#ffffff', '#fff3b0', '#ffd23a', '#b98a10', '#3e2a00'],
  water: ['#f2fdff', '#a8ecff', '#3fb3e6', '#1f6fa8', '#0b2f52'],
  /** 돌기둥 — 흙빛이 도는 회색 돌 (갈색이면 나무 그루터기처럼 보였다) */
  stone: ['#f3efe6', '#c9c0ae', '#948a78', '#5e5648', '#2a251e'],
  /** 소한공 — 윤솔 시트의 한기 색 (옅은 하늘 → 짙은 남색) */
  ice: ['#f4fbff', '#c8e6ff', '#7fb2f0', '#3f6fc0', '#1a2f66'],
  /** 적소하 옷의 진홍 (pixelfx RAMPS.red 와 같다) */
  crimson: RAMPS.red,
} as const;

export type FxRamp = keyof typeof FX_RAMPS;

export type BurstStyle = 'ring' | 'fire' | 'air' | 'spark' | 'claw' | 'ember';

// ── 공용 도구 ─────────────────────────────────────────────

function cols(ramp: readonly string[]): RGB[] {
  return ramp.map(hex);
}

function clamp(v: number, a: number, b: number): number {
  return v < a ? a : v > b ? b : v;
}

/** k(0=심지 ~ 1=가장자리) 에 맞는 램프 색 */
function pick(C: RGB[], k: number): RGB {
  return C[clamp(Math.floor(k * C.length), 0, C.length - 1)];
}

/** 4x4 베이어 행렬. 반투명 대신 도트를 성글게 찍어 비쳐 보이게 한다. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);
function dither(x: number, y: number, density: number): boolean {
  return density > BAYER[((y & 3) << 2) | (x & 3)];
}

/** 무늬 없이 흩뿌린 점. 넓은 면을 성글게 채울 때 쓴다(베이어는 넓게 깔면 격자가 보인다). */
function speck(x: number, y: number, density: number, seed = 0): boolean {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(seed | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296 < density;
}

/**
 * 시전자(또는 중심)를 원점으로 하는 도화지.
 * 좌표 범위를 주면 필요한 만큼만 캔버스를 잡는다. +x 가 앞이다.
 */
class Canvas {
  readonly buf: PixelBuf;
  private readonly ox: number;
  private readonly oy: number;
  /** 0보다 크면 put 할 때마다 이 확률로 건너뛴다(흩어지며 사라지는 연출) */
  drop = 0;
  private readonly rnd: () => number;

  constructor(xmin: number, xmax: number, ymin: number, ymax: number, seed = 1, pad = 3) {
    const x0 = Math.floor(xmin) - pad;
    const x1 = Math.ceil(xmax) + pad;
    const y0 = Math.floor(ymin) - pad;
    const y1 = Math.ceil(ymax) + pad;
    this.buf = new PixelBuf(x1 - x0 + 1, y1 - y0 + 1);
    this.ox = -x0;
    this.oy = -y0;
    this.rnd = rand(seed);
  }

  get w(): number {
    return this.buf.w;
  }

  get h(): number {
    return this.buf.h;
  }

  put(x: number, y: number, c: RGB): void {
    if (this.drop > 0 && this.rnd() < this.drop) return;
    this.buf.set(Math.round(x) + this.ox, Math.round(y) + this.oy, c);
  }

  disc(x: number, y: number, r: number, c: RGB): void {
    if (r < 0.75) {
      this.put(x, y, c);
      return;
    }
    // put 을 칸마다 부르던 것을 풀어 썼다 — 같은 칸을 같은 순서로 찍고 흩어지기 난수도 같은 순서로 쓴다(그림 같음)
    const n = Math.ceil(r);
    const lim = r * r + 0.3;
    const drop = this.drop;
    const buf = this.buf;
    const ox = this.ox;
    const oy = this.oy;
    for (let dy = -n; dy <= n; dy++) {
      const py = Math.round(y + dy) + oy;
      const dy2 = dy * dy;
      for (let dx = -n; dx <= n; dx++) {
        if (dx * dx + dy2 > lim) continue;
        if (drop > 0 && this.rnd() < drop) continue;
        buf.set(Math.round(x + dx) + ox, py, c);
      }
    }
  }

  /** 굵기 w 짜리 선 */
  line(x0: number, y0: number, x1: number, y1: number, c: RGB, w = 1): void {
    const n = Math.ceil(Math.hypot(x1 - x0, y1 - y0) * 1.4) + 1;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = x0 + (x1 - x0) * t;
      const y = y0 + (y1 - y0) * t;
      if (w <= 1) this.put(x, y, c);
      else this.disc(x, y, w / 2, c);
    }
  }

  /** 꺾인 선(점 목록)을 잇는다 */
  path(pts: Array<[number, number]>, c: RGB, w = 1): void {
    for (let i = 1; i < pts.length; i++) this.line(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1], c, w);
  }

  /** 캔버스 전체 픽셀을 훑는다. 좌표는 원점 기준 정수. 색을 돌려주면 찍는다. */
  paint(fn: (x: number, y: number) => RGB | null): void {
    for (let py = 0; py < this.buf.h; py++) {
      for (let px = 0; px < this.buf.w; px++) {
        const c = fn(px - this.ox, py - this.oy);
        if (c) {
          if (this.drop > 0 && this.rnd() < this.drop) continue;
          this.buf.set(px, py, c);
        }
      }
    }
  }

  /**
   * paint 와 똑같다(같은 순서로 훑고, 같은 칸에 같은 색, 흩어지기 난수도 같은 순서로 쓴다).
   * 다만 fn 이 픽셀 번호(py·w + px)를 받는다 — 미리 구해 둔 표(polar)를 여러 장면이 같이 쓰게.
   */
  paintIdx(fn: (i: number) => RGB | null): void {
    const w = this.buf.w;
    const h = this.buf.h;
    for (let py = 0, i = 0; py < h; py++) {
      for (let px = 0; px < w; px++, i++) {
        const c = fn(i);
        if (c) {
          if (this.drop > 0 && this.rnd() < this.drop) continue;
          this.buf.set(px, py, c);
        }
      }
    }
  }

  /** 각 픽셀의 원점까지 거리와 각도 비율(0~1, paint 에서 쓰던 식 그대로). 크기가 같은 판끼리 한 번만 구해 나눠 쓴다 */
  polar(): { d: Float64Array; a: Float64Array } {
    const w = this.buf.w;
    const h = this.buf.h;
    const d = new Float64Array(w * h);
    const a = new Float64Array(w * h);
    for (let py = 0, i = 0; py < h; py++) {
      const y = py - this.oy;
      for (let px = 0; px < w; px++, i++) {
        const x = px - this.ox;
        d[i] = Math.hypot(x, y);
        a[i] = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
      }
    }
    return { d, a };
  }

  get anchorX(): number {
    return (this.ox + 0.5) / this.buf.w;
  }

  get anchorY(): number {
    return (this.oy + 0.5) / this.buf.h;
  }

  toTexture(): Texture {
    return this.buf.toTexture();
  }
}

/** 부채꼴(+x 중심, 전체 각 arcDeg)을 담는 좌표 범위 */
function sectorBounds(R: number, arcDeg: number, margin = 4): { xmin: number; xmax: number; ymin: number; ymax: number } {
  const h = Math.min(Math.PI, (arcDeg * Math.PI) / 360);
  const xmin = h > Math.PI / 2 ? R * Math.cos(h) : 0;
  const ymax = h >= Math.PI / 2 ? R : R * Math.sin(h);
  return { xmin: Math.min(0, xmin) - margin, xmax: R + margin, ymin: -ymax - margin, ymax: ymax + margin };
}

/** 두 점 사이를 번개처럼 꺾는다(중점 변위). */
export function jagged(
  x0: number, y0: number, x1: number, y1: number, amp: number, levels: number, rnd: () => number,
): Array<[number, number]> {
  let pts: Array<[number, number]> = [[x0, y0], [x1, y1]];
  let a = amp;
  for (let l = 0; l < levels; l++) {
    const next: Array<[number, number]> = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1];
      const [bx, by] = pts[i];
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const o = (rnd() - 0.5) * 2 * a;
      next.push([(ax + bx) / 2 - (dy / len) * o, (ay + by) / 2 + (dx / len) * o]);
      next.push(pts[i]);
    }
    pts = next;
    a *= 0.55;
  }
  return pts;
}

// ═══════════════════════════════════════════════════════════
//  베기 (melee) — +x 를 향하고 기준점은 시전자
// ═══════════════════════════════════════════════════════════

export function swingFrames(fx: string, radius: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  switch (fx) {
    case 'frostfan': return frostFan(radius, arcDeg, ramp);
    case 'lash': return lash(radius, arcDeg, ramp);
    case 'windcut': return windCut(radius, arcDeg, ramp);
    case 'hammer': return hammer(radius, arcDeg, ramp);
    case 'thrust': return thrust(radius, arcDeg, ramp);
    case 'dash': return dash(radius, arcDeg, ramp);
    case 'greatsword': return greatsword(radius, arcDeg);
    case 'whirl': return whirl(radius, arcDeg);
    default: return crescent({ radius, arcDeg, ramp: [...ramp], frames: 5 });
  }
}

/** 서리 대검 — 부채꼴 안에서 얼음 가시가 뻗었다가 부서져 흩날린다. */
function frostFan(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const h = (arcDeg * Math.PI) / 360;
  const n = 6;
  const r0 = R * 0.14;
  const count = clamp(Math.round(arcDeg / 10), 4, 16);
  const rnd = rand(9001 + Math.round(R) * 7 + count);
  const spikes: Array<{ a: number; len: number; base: number }> = [];
  const step = (2 * h) / count;
  for (let i = 0; i < count; i++) {
    const a = -h + step * (i + 0.5) + (rnd() - 0.5) * step * 0.5;
    const centre = 1 - 0.2 * (Math.abs(a) / Math.max(h, 0.01)); // 가운데 가시가 더 길다
    spikes.push({ a, len: R * (0.72 + 0.28 * rnd()) * centre, base: 2 + R * 0.022 + rnd() * 1.4 });
  }
  const b = sectorBounds(R, arcDeg, 8);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const grow = 1 - Math.pow(1 - Math.min(1, (t + 0.12) / 0.42), 2);
    const shatter = Math.max(0, (t - 0.45) / 0.55);
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 311 + f * 29);
    const rr = rand(77 + f * 13);
    for (const s of spikes) {
      const ca = Math.cos(s.a);
      const sa = Math.sin(s.a);
      const tip = r0 + (s.len - r0) * grow;
      const drift = shatter * 8;
      for (let d = r0; d <= tip; d += 0.5) {
        const u = (d - r0) / Math.max(1, tip - r0);
        const w = s.base * (1 - u) + 0.4;
        for (let o = -w; o <= w; o += 0.5) {
          if (shatter > 0 && rr() < shatter * 0.8) continue;
          const k = Math.abs(o) / w;
          const c = k > 0.82 ? C[4] : k > 0.55 ? C[3] : k > 0.22 ? C[2] : u > 0.55 ? C[0] : C[1];
          const dd = d + drift * (0.3 + u);
          cv.put(ca * dd - sa * o, sa * dd + ca * o, c);
        }
      }
    }
    // 깨진 결정 가루
    const dust = Math.round(count * (0.6 + shatter * 4));
    for (let i = 0; i < dust; i++) {
      const a = -h + rr() * 2 * h;
      const d = r0 + rr() * R * (0.45 + 0.6 * t);
      cv.put(Math.cos(a) * d, Math.sin(a) * d, rr() < 0.55 ? C[0] : C[1]);
    }
    // 뿌리 쪽에 서린 냉기
    if (t < 0.5) {
      for (let a = -h; a <= h; a += 0.6 / Math.max(r0, 1)) {
        if (rr() < 0.5) continue;
        cv.put(Math.cos(a) * r0 * 0.9, Math.sin(a) * r0 * 0.9, C[1]);
      }
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 16 };
}

/**
 * 화염 채찍 받은 그림 (assets/fx/fire_whip.png + .json, tools/build_fx.py --whip).
 * 장면들이 가로로 붙어 있고, 장면마다 채찍이 그리는 호의 중심이 (cx, cy) 에 오게 맞춰 두었다.
 * core = 다 펼친 채찍 심지의 반지름, reach = 그림이 중심에서 가장 멀리 닿는 거리,
 * arcFrom → arcTo = 다 펼친 채찍이 덮는 각(화면 좌표, 시계 방향으로 커진다). 전부 그림 px.
 */
export interface WhipArt {
  frames: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
  core: number;
  reach: number;
  arcFrom: number;
  arcTo: number;
  /** 판 전체 RGBA (가로 w × frames, 세로 h) */
  px: Uint8ClampedArray;
}

let whipArt: WhipArt | null = null;

export function setWhipArt(art: WhipArt | null): void {
  whipArt = art;
}

/** 채찍 심지가 판정 반경의 이만큼에 온다 */
const WHIP_CORE = 0.74;
/**
 * 심지 바깥으로 뻗는 불길은 점점 눌러 그린다: 심지의 WHIP_KNEE 배까지는 그대로, 그 바깥은
 * knee + WHIP_ROOM·tanh(넘은 만큼 / WHIP_ROOM) — 가까운 불길은 거의 안 눌리고 먼 불길일수록 더 눌린다.
 * 그림에서 심지의 WHIP_FADE 배보다 먼 것(뻗어 나가는 채찍 끝, 멀리 튄 불티)은 누르면 가는 줄무늬가 되므로
 * 누르지 않고 흩어져 사라지게 한다 → 모든 장면이 판정 반경의 약 1.1배 안, 불길 95% 는 반경 안.
 */
const WHIP_KNEE = 1.0;
const WHIP_ROOM = 0.5;
const WHIP_FADE = [1.45, 1.85] as const;
/** 채찍 불투명도 — 후반에 이펙트가 겹쳐도 적이 보이게 */
const WHIP_ALPHA = 0.85;

/**
 * 받은 그림으로 굽는 화염 채찍. 그림을 극좌표로 휘어 붙인다:
 * 다 펼친 채찍의 호(그림에선 약 200°)가 지금 훑는 각(200~280°)에 딱 맞게 늘고, 심지가 반경의 WHIP_CORE 에 온다.
 * 호 밖(감긴 채찍·손잡이 쪽)은 남은 각에 나눠 담는다.
 */
function lashArt(R: number, arcDeg: number, art: WhipArt): FxFrames {
  const h = Math.min(Math.PI * 0.97, (arcDeg * Math.PI) / 360);
  const half = (art.arcTo - art.arcFrom) / 2;
  const mid = art.arcFrom + half;
  const coreG = R * WHIP_CORE;
  const s = art.core / coreG; // 게임 1px 이 그림 몇 px 인가
  const knee = coreG * WHIP_KNEE;
  const room = coreG * WHIP_ROOM;
  const lin = Math.min(art.reach / s, coreG * WHIP_FADE[1]);
  const reach = lin <= knee ? lin : knee + room * Math.tanh((lin - knee) / room);
  const n = Math.ceil(reach) + 2;
  const side = 2 * n + 1;
  // 게임 칸마다 그림의 어느 칸을 가져올지 한 번만 셈한다 (장면마다 같다)
  const src = new Int32Array(side * side).fill(-1);
  const sheetW = art.w * art.frames;
  for (let py = 0, i = 0; py < side; py++) {
    const y = py - n;
    for (let px = 0; px < side; px++, i++) {
      const x = px - n;
      const r = Math.hypot(x, y);
      if (r > reach) continue;
      const rl = r <= knee ? r : knee + room * Math.atanh(Math.min(0.999999, (r - knee) / room));
      const u = rl / coreG; // 그림에서 심지의 몇 배 거리인가
      if (u > WHIP_FADE[1] || (u > WHIP_FADE[0] && !speck(px, py, (WHIP_FADE[1] - u) / (WHIP_FADE[1] - WHIP_FADE[0]), 31))) continue;
      const t = Math.atan2(y, x);
      const at = Math.abs(t);
      const o = at <= h ? (t * half) / h : Math.sign(t) * (half + ((at - h) * (Math.PI - half)) / (Math.PI - h));
      const phi = mid + o;
      const sx = Math.round(art.cx + rl * s * Math.cos(phi));
      const sy = Math.round(art.cy + rl * s * Math.sin(phi));
      if (sx < 0 || sy < 0 || sx >= art.w || sy >= art.h) continue;
      src[i] = sy * sheetW + sx;
    }
  }
  // 모든 장면을 담는 가장 작은 네모로 잘라 굽는다 (기준점이 장면마다 같아야 해서 한 네모)
  let x0 = side;
  let x1 = -1;
  let y0 = side;
  let y1 = -1;
  for (let f = 0; f < art.frames; f++) {
    const off = f * art.w;
    for (let i = 0; i < src.length; i++) {
      if (src[i] < 0 || art.px[(src[i] + off) * 4 + 3] < 128) continue;
      const px = i % side;
      const py = (i - px) / side;
      if (px < x0) x0 = px;
      if (px > x1) x1 = px;
      if (py < y0) y0 = py;
      if (py > y1) y1 = py;
    }
  }
  x0 = Math.min(x0, n) - 1;
  y0 = Math.min(y0, n) - 1;
  x1 = Math.max(x1, n) + 1;
  y1 = Math.max(y1, n) + 1;
  const bw = x1 - x0 + 1;
  const bh = y1 - y0 + 1;
  const textures: Texture[] = [];
  for (let f = 0; f < art.frames; f++) {
    const off = f * art.w;
    const buf = new PixelBuf(bw, bh);
    const d = buf.data;
    for (let py = y0; py <= y1; py++) {
      for (let px = x0; px <= x1; px++) {
        const i = py * side + px;
        if (i < 0 || py >= side || px < 0 || px >= side || src[i] < 0) continue;
        const k = (src[i] + off) * 4;
        if (art.px[k + 3] < 128) continue;
        const j = ((py - y0) * bw + (px - x0)) * 4;
        d[j] = art.px[k];
        d[j + 1] = art.px[k + 1];
        d[j + 2] = art.px[k + 2];
        d[j + 3] = 255;
      }
    }
    textures.push(buf.toTexture());
  }
  return { textures, anchorX: (n - x0 + 0.5) / bw, anchorY: (n - y0 + 0.5) / bh, fps: 22, alpha: WHIP_ALPHA };
}

/** 화염 채찍 — 채찍이 부채꼴을 가로질러 휘감고, 끝이 지나간 자리에 불길 띠가 남는다. 받은 그림이 있으면 그것으로(lashArt). */
function lash(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  if (whipArt) return lashArt(R, arcDeg, whipArt);
  const C = cols(ramp);
  const h = Math.min(Math.PI, (arcDeg * Math.PI) / 360);
  const n = 6;
  const b = sectorBounds(R, arcDeg, 8);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const sweep = Math.min(1, (t + 0.08) / 0.68);
    const e = 1 - (1 - sweep) * (1 - sweep);
    const tipA = -h + 2 * h * e;
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 1201 + f);
    const rr = rand(4100 + f * 31);

    // 1) 끝이 훑고 간 호를 따라 불길 띠 — 방금 지나간 곳일수록 두껍고 밝다
    const late = Math.max(0, (t - 0.5) / 0.5);
    for (let a = -h; a <= tipA; a += 0.5 / R) {
      const age = (tipA - a) / Math.max(0.01, 2 * h);
      const mid = R * 0.9 - age * R * 0.06;
      const th = (R * 0.075 * (1 - age * 0.7) + 1) * (1 - late * 0.55);
      for (let o = -th; o <= th; o += 0.7) {
        const q = Math.abs(o) / th;
        if (rr() < 0.12 + age * 0.5 + late * 0.35 + q * 0.3) continue;
        const d = mid + o + (rr() - 0.5) * 2;
        cv.put(Math.cos(a) * d, Math.sin(a) * d, pick(C, 0.05 + age * 0.55 + q * 0.35));
      }
      // 바깥으로 튀는 불티
      if (rr() < 0.05 * (1 - age)) {
        const d = mid + th + 2 + rr() * 6;
        cv.put(Math.cos(a) * d, Math.sin(a) * d, C[1]);
      }
    }

    // 2) 채찍 몸통 — 손잡이는 앞서고 끝은 늦게 따라오다 마지막에 꺾인다
    if (t < 0.9) {
      const pts: Array<[number, number, number]> = [];
      const wob = (1 - t) * 4;
      for (let u = 0; u <= 1.0001; u += 0.5 / R) {
        const lag = (1 - u) * (1 - u) * 0.85 * (1 - e * 0.55);
        const a = tipA - lag;
        const rad = R * (0.1 + 0.9 * u);
        const off = Math.sin(u * Math.PI * 3 - t * 9) * wob * u;
        pts.push([Math.cos(a) * rad - Math.sin(a) * off, Math.sin(a) * rad + Math.cos(a) * off, 3.2 - 1.8 * u]);
      }
      for (const [x, y, w] of pts) cv.disc(x, y, w, C[4]);
      for (const [x, y, w] of pts) cv.disc(x, y, Math.max(0.5, w - 1), C[2]);
      for (const [x, y, w] of pts) cv.disc(x, y, Math.max(0.3, w - 2), C[1]);
      // 3) 채찍 끝이 꺾이는 순간의 섬광
      if (t >= 0.4 && t <= 0.8) {
        const [x, y] = pts[pts.length - 1];
        for (let k = -6; k <= 6; k++) {
          const c = Math.abs(k) < 3 ? C[0] : C[1];
          cv.put(x + k, y, c);
          cv.put(x, y + k, c);
          if (Math.abs(k) < 4) {
            cv.put(x + k, y + k, C[1]);
            cv.put(x + k, y - k, C[1]);
          }
        }
      }
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 18 };
}

/** 날바람 — 가느다란 칼바람 세 줄이 발톱처럼 긋고 바깥으로 밀려난다. */
function windCut(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const h = Math.min(Math.PI, (arcDeg * Math.PI) / 360);
  const n = 5;
  const lines = [
    { r: 0.62, da: -0.09 },
    { r: 0.8, da: 0 },
    { r: 0.97, da: 0.09 },
  ];
  const b = sectorBounds(R * 1.12, arcDeg + 12, 6);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const reveal = Math.min(1, (t + 0.3) / 0.55);
    const push = t * R * 0.1;
    const erode = Math.max(0, (t - 0.45) / 0.55);
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 88 + f);
    const rr = rand(6200 + f * 11);
    for (const ln of lines) {
      const rad = R * ln.r + push;
      const a0 = -h * 0.92 + ln.da;
      const span = 1.84 * h;
      for (let a = a0; a <= a0 + span * reveal; a += 0.45 / rad) {
        const u = (a - a0) / span;
        if (u < erode) continue;
        const th = 1.9 * Math.sin(Math.PI * u);
        const x = Math.cos(a) * rad;
        const y = Math.sin(a) * rad;
        cv.put(x, y, th > 1.1 ? C[0] : C[1]);
        if (th > 0.9) {
          cv.put(Math.cos(a) * (rad - 1), Math.sin(a) * (rad - 1), C[2]);
          if (th > 1.5) cv.put(Math.cos(a) * (rad + 1), Math.sin(a) * (rad + 1), C[1]);
        }
      }
    }
    // 바람 부스러기
    if (t >= 0.25) {
      for (let i = 0; i < 6; i++) {
        const a = -h + rr() * 2 * h;
        const d = R * (1.0 + rr() * 0.1) + push;
        const len = 3 + rr() * 4;
        for (let k = 0; k < len; k++) {
          const aa = a + k / d;
          cv.put(Math.cos(aa) * d, Math.sin(aa) * d, C[2]);
        }
      }
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 20 };
}

/** 화강암 망치 — 앞에 내리찍혀 땅이 패고, 금이 가고, 돌이 튄다. */
function hammer(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 6;
  const px = R * 0.6;
  const cr = Math.max(8, R * 0.24);
  const rnd = rand(5150 + Math.round(R));
  const h = (Math.min(arcDeg, 160) * Math.PI) / 360;

  const cracks: Array<Array<[number, number]>> = [];
  for (let j = 0; j < 7; j++) {
    const back = j >= 5;
    const a = back ? Math.PI + (rnd() - 0.5) * 1.2 : (rnd() - 0.5) * 2 * (h + 0.5);
    const len = R * (back ? 0.22 : 0.32 + rnd() * 0.2);
    cracks.push(jagged(px, 0, px + Math.cos(a) * len, Math.sin(a) * len, 3, 3, rnd));
  }
  const debris: Array<{ a: number; v: number; s: number }> = [];
  for (let j = 0; j < 12; j++) debris.push({ a: (rnd() - 0.5) * 2 * (h + 0.9), v: R * (0.3 + rnd() * 0.35), s: rnd() < 0.4 ? 2 : 1 });

  const reach = px + R * 0.6;
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const cv = new Canvas(-R * 0.2, reach, -R * 0.62, R * 0.62, 500 + f);
    const tau = Math.max(0, (f - 1) / (n - 2));
    if (f === 0) {
      // 내려오는 망치 머리 (위에서 본 네모) + 자루
      cv.line(6, 0, px, 0, C[3], 3);
      const hw = cr * 0.55;
      const hh = cr * 0.95;
      for (let y = -hh; y <= hh; y++) {
        for (let x = -hw; x <= hw; x++) {
          const edge = Math.abs(x) > hw - 1.5 || Math.abs(y) > hh - 1.5;
          cv.put(px + x, y, edge ? C[4] : x + y < 0 ? C[1] : C[2]);
        }
      }
    } else {
      if (f === n - 1) cv.drop = 0.5;
      // 금
      const cl = f === 1 ? 0.5 : 1;
      for (const c of cracks) {
        const m = Math.max(2, Math.round(c.length * cl));
        const part = c.slice(0, m);
        cv.path(part, C[4], 2);
        for (const [x, y] of part) cv.put(x - 1, y - 1, C[2]);
      }
      // 먼지 고리
      const dr = cr * (1.2 + 1.3 * tau);
      for (let a = 0; a < Math.PI * 2; a += 0.7 / dr) {
        const x = px + Math.cos(a) * dr;
        const y = Math.sin(a) * dr;
        if (dither(Math.round(x), Math.round(y), 0.55 * (1 - tau))) cv.put(x, y, C[1]);
      }
      // 패인 자리
      const r = f === 1 ? cr * 1.1 : cr * 0.85;
      cv.disc(px, 0, r, f === 1 ? C[1] : C[3]);
      if (f === 1) cv.disc(px, 0, r * 0.6, C[0]);
      else {
        for (let y = -r; y <= r; y++) {
          for (let x = -r; x <= r; x++) {
            if (x * x + y * y > r * r) continue;
            if (speck(Math.round(px + x), Math.round(y), 0.5, 3)) cv.put(px + x, y, C[4]);
          }
        }
        for (let a = Math.PI * 0.9; a < Math.PI * 1.9; a += 0.8 / r) cv.put(px + Math.cos(a) * r, Math.sin(a) * r, C[1]);
      }
      // 튀는 돌
      for (const d of debris) {
        const dist = cr * 0.6 + d.v * tau;
        const x = px + Math.cos(d.a) * dist;
        const y = Math.sin(d.a) * dist;
        if (d.s === 2) {
          cv.disc(x, y, 1.5, C[4]);
          cv.put(x, y, C[2]);
        } else cv.put(x, y, C[3]);
      }
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 14 };
}

/** 화염의 랜스 — 길고 가는 불의 창이 쭉 뻗었다가 불티로 흩어진다. */
function thrust(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const r0 = 10;
  const wmax = clamp(R * Math.tan((Math.min(arcDeg, 60) * Math.PI) / 360) * 0.5, 3, 11);
  const ext = [0.55, 1, 1, 1, 1];
  const thin = [1, 1, 0.78, 0.5, 0.25];
  const embers = [0, 0.25, 0.55, 0.85, 1];
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < ext.length; f++) {
    const cv = new Canvas(0, R + 4, -(wmax + 12), wmax + 12, 70 + f);
    const rr = rand(3300 + f * 7);
    const tip = r0 + (R - r0) * ext[f];
    const peak = r0 + (tip - r0) * 0.22;
    if (f >= 3) cv.drop = 0.25;
    const widthAt = (x: number): number => {
      const w = x < peak ? wmax * Math.sqrt(Math.max(0, (x - r0) / (peak - r0))) : wmax * Math.pow(Math.max(0, (tip - x) / (tip - peak)), 0.9);
      return w * thin[f];
    };
    for (let x = r0; x <= tip; x++) {
      const w = widthAt(x) * (0.85 + rr() * 0.3);
      for (let y = -Math.ceil(w); y <= Math.ceil(w); y++) {
        const k = Math.abs(y) / Math.max(0.6, w);
        if (k > 1) continue;
        cv.put(x, y, k < 0.3 ? C[0] : k < 0.6 ? C[1] : k < 0.85 ? C[2] : C[3]);
      }
      if (w > 1.2) {
        cv.put(x, -Math.ceil(w) - 1, C[4]);
        cv.put(x, Math.ceil(w) + 1, C[4]);
      }
    }
    cv.drop = 0;
    if (f <= 2) cv.disc(tip, 0, 1.6, C[0]);
    const m = Math.round((R / 5) * embers[f]);
    for (let i = 0; i < m; i++) {
      const x = r0 + rr() * (tip - r0);
      const side = rr() < 0.5 ? -1 : 1;
      const y = side * (widthAt(x) + 1 + rr() * 9 * embers[f]);
      cv.put(x, y, pick(C, 0.2 + rr() * 0.6));
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 18 };
}

/** 전격 질주 — 눈 깜짝할 새 앞을 곧게 긋는 섬광. 옆으로 속도선이 흐르고 불똥이 튄다. */
function dash(R: number, arcDeg: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const spread = (Math.min(arcDeg, 100) * Math.PI) / 180;
  const n = 4;
  const ext = [0.6, 1, 1, 1];
  const thin = [1, 1, 0.65, 0.35];
  const drops = [0, 0, 0.25, 0.65];
  const b = sectorBounds(R + 4, Math.min(arcDeg, 100), 8);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 40 + f);
    const rr = rand(700 + f * 17);
    const tip = 6 + (R - 6) * ext[f];
    cv.drop = drops[f];
    // 속도선 — 부채꼴 폭만큼 벌어진 가는 점선
    for (const k of [-0.42, -0.22, 0.22, 0.42]) {
      const a = k * spread;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const x0 = R * (0.22 + rr() * 0.1);
      const x1 = Math.min(tip, R * (0.62 + rr() * 0.3));
      for (let x = x0; x <= x1; x++) {
        if ((Math.round(x) + f * 2) % 5 === 0) continue;
        cv.put(ca * x, sa * x, (x - x0) / (x1 - x0) > 0.6 ? C[0] : C[1]);
      }
    }
    // 본 섬광 — 양 끝이 가늘고 가운데가 두툼한 곧은 줄
    for (let x = 6; x <= tip; x++) {
      const u = (x - 6) / Math.max(1, tip - 6);
      const w = (0.8 + 3.8 * Math.sin(Math.PI * Math.min(1, u * 1.04))) * thin[f];
      for (let y = -Math.ceil(w) - 1; y <= Math.ceil(w) + 1; y++) {
        const k = Math.abs(y) / Math.max(0.5, w);
        if (k > 1.35) continue;
        cv.put(x, y, k < 0.35 ? C[0] : k < 0.8 ? C[1] : C[3]);
      }
    }
    cv.drop = 0;
    if (f <= 1) cv.disc(tip, 0, 2, C[0]);
    // 튀는 불똥 (작은 번개 조각)
    const sparks = f === 0 ? 2 : 6;
    for (let i = 0; i < sparks; i++) {
      const x = 10 + rr() * (tip - 10);
      const side = rr() < 0.5 ? -1 : 1;
      let px = x;
      let py = side * 3;
      for (let k = 0; k < 3; k++) {
        const nx = px + (rr() - 0.5) * 5;
        const ny = py + side * (2 + rr() * 3);
        cv.line(px, py, nx, ny, k === 0 ? C[0] : C[1], 1);
        px = nx;
        py = ny;
      }
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 22 };
}

/** 회광반조 — 잿빛 대검의 두꺼운 반달. 바깥 날에 불이 붙어 있고 불똥이 튄다. */
function greatsword(R: number, arcDeg: number): FxFrames {
  const A = cols(FX_RAMPS.ash);
  const F = cols(FX_RAMPS.flame);
  const n = 6;
  const span = (arcDeg * Math.PI) / 180;
  const b = sectorBounds(R * 1.15, arcDeg + 10, 6);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const rmid = R * (0.52 + 0.43 * t);
    const half = R * (0.1 + 0.16 * Math.sin(Math.PI * Math.min(1, t * 1.15))) + 2;
    const sp = span * (0.8 + 0.3 * t);
    const sparse = Math.max(0, (t - 0.5) / 0.5) * 0.8;
    const rr = rand(2600 + f * 19);
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 9 + f);
    cv.paint((x, y) => {
      const d = Math.hypot(x, y);
      if (d < 1) return null;
      const u = Math.abs(Math.atan2(y, x)) / (sp / 2);
      if (u > 1) return null;
      const thick = half * Math.sqrt(Math.max(0, 1 - u * u));
      if (thick < 0.6) return null;
      const off = d - rmid;
      if (Math.abs(off) > thick) return null;
      const k = Math.abs(off) / thick;
      if (sparse > 0 && rr() < sparse * k) return null;
      if (off > thick * 0.3) return k > 0.9 ? F[3] : k > 0.68 ? F[2] : F[1];   // 불붙은 바깥 날
      if (off < -thick * 0.78) return A[4];                                     // 안쪽 그림자
      return k < 0.25 ? A[0] : k < 0.55 ? A[1] : A[2];                           // 잿빛 칼몸
    });
    const m = Math.round(34 * t + 6);
    for (let i = 0; i < m; i++) {
      const a = (rr() - 0.5) * sp * 1.05;
      const d = rmid + half * 0.8 + rr() * R * (0.08 + 0.3 * t);
      if (rr() < 0.35) cv.disc(Math.cos(a) * d, Math.sin(a) * d, 1, F[2]);
      cv.put(Math.cos(a) * d, Math.sin(a) * d, F[Math.floor(rr() * 2)]);
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 17 };
}

/** 광풍 — 불·서리·전하 세 가닥이 꼬여서 소용돌이 팔 하나가 된다. 네 방향이면 바람개비. */
function whirl(R: number, arcDeg: number): FxFrames {
  const strands = [cols(FX_RAMPS.flame), cols(RAMPS.blue), cols(RAMPS.yellow)];
  const G = cols(RAMPS.grey);
  const h = Math.min(Math.PI, (arcDeg * Math.PI) / 360);
  const n = 5;
  const b = sectorBounds(R * 1.12, arcDeg + 30, 8);
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const rot = t * 0.55;
    const reveal = Math.min(1, 0.35 + t * 1.4);
    const cv = new Canvas(b.xmin, b.xmax, b.ymin, b.ymax, 60 + f);
    cv.drop = Math.max(0, (t - 0.6) / 0.4) * 0.75;
    const rr = rand(8800 + f * 3);
    // 바람 부스러기
    for (let i = 0; i < 30; i++) {
      const a = -h + rr() * 2 * h;
      const d = R * (0.3 + rr() * 0.75);
      cv.put(Math.cos(a) * d, Math.sin(a) * d, G[rr() < 0.5 ? 1 : 2]);
    }
    strands.forEach((C, k) => {
      for (let u = 0; u <= reveal; u += 0.4 / R) {
        const a = -h + 2 * h * u + rot * (1 - u) - 0.25;
        const rad = R * (0.25 + 0.72 * u) + Math.sin(u * Math.PI * 4 + (k * Math.PI * 2) / 3 + t * 6) * R * 0.07;
        const w = 1 + 1.4 * Math.sin(Math.PI * u);
        const x = Math.cos(a) * rad;
        const y = Math.sin(a) * rad;
        cv.disc(x, y, w, C[2]);
        cv.disc(x, y, w * 0.45, C[1]);
      }
    });
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay, fps: 18 };
}

// ═══════════════════════════════════════════════════════════
//  탄 (projectile / nova) — +x 를 향한다. 여러 장이면 번갈아 보여 준다(깜빡임).
// ═══════════════════════════════════════════════════════════

/** width 는 좌우 반폭(px). 0이 아니면 그 폭에 맞춰 옆으로 긴 모양을 굽는다(파도). */
export function projectileFrames(fx: string, size: number, ramp: readonly string[], width = 0): Texture[] {
  const r = Math.max(3, Math.round(size));
  const rp = [...ramp];
  switch (fx) {
    case 'ember': return [0, 1, 2].map((s) => bolt('ember', r, rp, s));
    case 'shard': return [shard(r, ramp)];
    case 'dart': return [0, 1, 2].map((s) => dart(r, ramp, s));
    case 'lightning': return [0, 1, 2].map((s) => lightning(r, ramp, s));
    case 'bubble': return [bubble(r, ramp)];
    case 'chunk': return [rock(r, ramp)];
    case 'fireball': return [0, 1, 2].map((s) => fireball(r, ramp, s));
    case 'wave': return [0, 1, 2].map((s) => wave(r, ramp, s, width));
    case 'jet': return [0, 1].map((s) => jet(r, ramp, s));
    case 'quake': return [0, 1, 2].map((s) => quake(r, ramp, s));
    case 'rune': return [rune(r, ramp)];
    case 'royal': return [0, 1, 2].map((s) => royal(r, s));
    case 'puff': return [0, 1, 2].map((s) => puff(r, ramp, s));
    case 'gustarc': return [0, 1].map((s) => gustArc(r, ramp, s));
    case 'leaf': return [leaf(r, ramp)];
    case 'blade': return [blade(r, ramp)];
    default: {
      const shape: BoltShape =
        fx === 'spark' || fx === 'gust' || fx === 'chunk' || fx === 'shard' || fx === 'ember' ? fx : 'orb';
      return [bolt(shape, r, rp)];
    }
  }
}

/** 날카로운 얼음 — 마름모 빙편에 흰 결 한 줄 */
function shard(r: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-r * 2, r * 2, -r, r, 3);
  cv.paint((x, y) => {
    const m = Math.abs(x) / (r * 1.9) + Math.abs(y) / r;
    if (m > 1) return null;
    if (m > 0.86) return C[3];
    if (Math.abs(y) < 0.6 && x > -r) return C[0];
    return m < 0.45 ? C[1] : C[2];
  });
  return cv.toTexture();
}

/** 테이저 볼트 — 작고 밝은 전기 침. 몸에서 잔가지가 튄다. */
function dart(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const rr = rand(1500 + r * 13 + seed * 101);
  const cv = new Canvas(-r * 2.2, r * 1.3, -r * 1.2, r * 1.2, seed + 1);
  for (let x = -r * 2; x < 0; x++) {
    if (rr() < 0.25) continue;
    const k = -x / (r * 2);
    cv.put(x, Math.round((rr() - 0.5) * 2 * k), pick(C, 0.3 + k * 0.6));
  }
  for (let i = 0; i < 5; i++) {
    const a = rr() * Math.PI * 2;
    const l = r * (0.7 + rr() * 0.5);
    cv.path(jagged(0, 0, Math.cos(a) * l, Math.sin(a) * l, 1.5, 2, rr), C[2], 1);
  }
  cv.disc(0, 0, r * 0.45, C[1]);
  cv.disc(0, 0, r * 0.25, C[0]);
  return cv.toTexture();
}

/** 암석 파편 — 면마다 명암이 다른 각진 돌. 돌면서 날아간다. */
function rock(r: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const rr = rand(8200 + r);
  const verts = 6;
  const rs = Array.from({ length: verts }, () => r * (0.7 + rr() * 0.3));
  const cv = new Canvas(-r, r, -r, r, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
    const i = Math.floor(a * verts) % verts;
    const f = a * verts - Math.floor(a * verts);
    const lim = rs[i] * (1 - f) + rs[(i + 1) % verts] * f;
    if (d > lim) return null;
    if (d > lim - 1.2) return C[4];
    // 면마다 빛 받는 정도가 다르다 (왼쪽 위에서 빛)
    const face = Math.cos(((i + 0.5) / verts) * Math.PI * 2 - Math.PI - (-Math.PI * 3) / 4);
    return face > 0.5 ? C[1] : face > -0.2 ? C[2] : C[3];
  });
  cv.put(-r * 0.35, -r * 0.35, C[0]);
  return cv.toTexture();
}

/** 썬더브레이크 — 길고 굵은 벼락. 뒤로 가지가 갈라진다. */
function lightning(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const rr = rand(2700 + r * 7 + seed * 53);
  const len = r * 5.5;
  const cv = new Canvas(-len / 2, len / 2, -r * 1.2, r * 1.2, 5 + seed);
  const pts = jagged(-len / 2, 0, len / 2 - r * 0.3, 0, r * 0.85, 5, rr);
  for (let k = 0; k < 2; k++) {
    const [x, y] = pts[3 + Math.floor(rr() * (pts.length - 6))];
    cv.path(jagged(x, y, x - r * 1.2, y + (rr() < 0.5 ? -1 : 1) * r * 0.8, 2, 2, rr), C[2], 1);
  }
  cv.path(pts, C[3], 5);
  cv.path(pts, C[1], 3);
  cv.path(pts, C[0], 1);
  cv.disc(len / 2 - r * 0.3, 0, r * 0.38, C[1]);
  cv.disc(len / 2 - r * 0.3, 0, r * 0.2, C[0]);
  return cv.toTexture();
}

/** 압축탄 — 속이 빈 공기 방울. 안에서 바람이 소용돌이친다(탄이 돈다). */
function bubble(r: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-r, r, -r, r, 2);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    if (d > r) return null;
    if (d > r - 1.2) return C[3];
    if (d > r - 2.6) return C[1];
    const a = Math.atan2(y, x);
    const sw = (((a * 2 + d * 0.55) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    if (sw < 0.9) return C[0];
    return dither(x & 7, y & 7, 0.25) ? C[2] : null;
  });
  cv.disc(-r * 0.4, -r * 0.4, 1.2, C[0]);
  return cv.toTexture();
}

/** 파이어볼 — 둥근 불덩이에 뒤로 날리는 불꼬리 */
function fireball(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const rr = rand(3900 + r * 3 + seed * 71);
  const lobes = Array.from({ length: 9 }, () => 0.86 + rr() * 0.2);
  const cv = new Canvas(-r * 2.6, r * 1.1, -r * 1.1, r * 1.1, 17 + seed);
  // 꼬리 불길
  for (let k = 0; k < 3; k++) {
    const oy = (k - 1) * r * 0.45;
    const len = r * (1.4 + rr() * 0.9);
    for (let x = 0; x > -len; x--) {
      const u = -x / len;
      const w = r * 0.42 * (1 - u);
      const yc = oy * (0.6 + u) + Math.sin(u * 7 + seed * 2 + k) * 1.5;
      for (let y = -w; y <= w; y++) {
        if (rr() < u * 0.55) continue;
        cv.put(x, yc + y, pick(C, 0.4 + u * 0.55));
      }
    }
  }
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
    const rr2 = r * lobes[Math.floor(a * lobes.length) % lobes.length];
    if (d > rr2) return null;
    const k = d / rr2;
    return k > 0.9 ? C[4] : k > 0.75 ? C[3] : k > 0.5 ? C[2] : k > 0.28 ? C[1] : C[0];
  });
  return cv.toTexture();
}

/**
 * 넘실이는 파도 — 앞으로 볼록한 물마루. 앞머리에 흰 거품이 인다.
 * halfW(좌우 반폭)가 주어지면 그 폭만큼 옆으로 길고 납작한 물마루가 된다.
 */
function wave(r: number, ramp: readonly string[], seed: number, halfW = 0): Texture {
  const C = cols(ramp);
  const rr = rand(4400 + r + seed * 37 + Math.round(halfW));
  // 옆으로 길수록 덜 휘게: 반폭을 원호 반지름으로 맞추고 호의 각도를 줄인다
  const wide = halfW > r * 1.4;
  const amax = wide ? 0.72 : 1.0;
  const Rc = wide ? halfW / Math.sin(amax) : r * 1.7;
  const cx = wide ? r * 0.75 - Rc : -r * 1.1;
  const thick = r * 0.55;
  const back = cx + (Rc - thick - r * 0.6) * Math.cos(amax);
  const top = Rc * Math.sin(amax) + 2;
  const cv = new Canvas(Math.min(-r * 0.7, back), r * 0.75, -top, top, 2 + seed);
  cv.paint((x, y) => {
    const dx = x - cx;
    const d = Math.hypot(dx, y);
    const a = Math.atan2(y, dx);
    if (Math.abs(a) > amax) return null;
    const u = Math.abs(a) / amax;
    const edge = Rc - u * r * 0.35; // 양 끝이 뒤로 말린다
    if (d > edge || d < edge - thick * (1 - u * 0.55)) return null;
    const k = (edge - d) / thick; // 0 = 앞머리
    if (k < 0.18) return rr() < 0.7 ? C[0] : C[1];
    if (k < 0.4) return C[1];
    if (k < 0.7) return C[2];
    return C[3];
  });
  // 뒤로 흩어지는 물방울 (길수록 많이)
  const drops = wide ? Math.round(7 * (halfW / (r * 1.35))) : 7;
  for (let i = 0; i < drops; i++) {
    const a = (rr() - 0.5) * 1.6 * amax;
    const d = Rc - thick - 2 - rr() * r * 0.5;
    cv.put(cx + Math.cos(a) * d, Math.sin(a) * d, C[rr() < 0.5 ? 1 : 2]);
  }
  return cv.toTexture();
}

/** 제트기류 — 화면 끝까지 꿰뚫는 뾰족한 바늘과 뒤로 끌리는 바람결 */
function jet(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const len = r * 7;
  const cv = new Canvas(-len / 2, len / 2, -r * 0.7, r * 0.7, 1);
  for (const [oy, from, to] of [[-r * 0.45, -0.5, 0.05], [r * 0.45, -0.42, 0.12]] as const) {
    for (let x = len * from; x < len * to; x++) {
      if ((((Math.round(x) + seed * 3) % 7) + 7) % 7 < 2) continue;
      cv.put(x, oy, (x - len * from) / (len * (to - from)) > 0.6 ? C[1] : C[2]);
    }
  }
  for (let x = -len / 2; x <= len / 2; x++) {
    const u = (x + len / 2) / len;
    const w = u < 0.8 ? r * 0.3 * Math.pow(u / 0.8, 1.6) : r * 0.3 * (1 - (u - 0.8) / 0.2);
    for (let y = -Math.ceil(w); y <= Math.ceil(w); y++) {
      if (Math.abs(y) > w + 0.2) continue;
      cv.put(x, y, Math.abs(y) < w * 0.45 || u > 0.9 ? C[0] : C[1]);
    }
  }
  return cv.toTexture();
}

/** 지진 파동 — 땅이 앞으로 볼록하게 솟구치며 밀려간다. 뒤에 흙먼지가 남는다. */
function quake(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const rr = rand(5800 + r * 5 + seed * 19);
  const Rc = r * 2.1;
  const cx = -r * 1.5;
  const cv = new Canvas(-r * 1.2, r * 0.9, -r * 1.55, r * 1.55, 7 + seed);
  // 흙먼지 (뒤쪽)
  for (let i = 0; i < r * 2.2; i++) {
    const a = (rr() - 0.5) * 1.3;
    const d = Rc - 3 - rr() * r * 0.9;
    const x = cx + Math.cos(a) * d;
    const y = Math.sin(a) * d;
    if (speck(Math.round(x), Math.round(y), 0.6, seed)) cv.put(x, y, C[rr() < 0.5 ? 1 : 2]);
  }
  // 갈라진 땅 띠
  for (let a = -0.72; a <= 0.72; a += 0.6 / Rc) {
    for (let k = 0; k < 3; k++) {
      const d = Rc - 2 - k;
      if (rr() < 0.35) continue;
      cv.put(cx + Math.cos(a) * d, Math.sin(a) * d, C[3 + (k > 0 ? 1 : 0)]);
    }
  }
  // 솟은 바위 이빨 — 앞으로 뾰족
  const teeth = Math.max(5, Math.round((1.44 * Rc) / 5));
  for (let j = 0; j < teeth; j++) {
    const a = -0.7 + (1.4 * (j + 0.5)) / teeth + (rr() - 0.5) * 0.05;
    const hgt = 3 + rr() * (r * 0.35) * (1 - Math.abs(a) * 0.6);
    const base = 2.4;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    for (let s = 0; s <= hgt; s += 0.5) {
      const w = base * (1 - s / hgt);
      for (let o = -w; o <= w; o += 0.5) {
        const d = Rc + s;
        const c = o < -w * 0.4 ? C[1] : o > w * 0.5 ? C[3] : C[2];
        cv.put(cx + ca * d - sa * o, sa * d + ca * o, Math.abs(o) > w - 0.6 ? C[4] : c);
      }
    }
  }
  return cv.toTexture();
}

/** 캐스팅 — 작은 네 갈래 별 룬. 돌면서 날아간다. */
function rune(r: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-r - 2, r + 2, -r - 2, r + 2, 1);
  cv.paint((x, y) => {
    const q = Math.sqrt(Math.abs(x)) + Math.sqrt(Math.abs(y));
    const lim = Math.sqrt(r);
    const d = Math.hypot(x, y);
    if (q <= lim) return q < lim * 0.55 ? C[0] : q < lim * 0.8 ? C[1] : C[3];
    if (Math.abs(d - (r * 0.75 + 1)) < 0.6 && ((x + y) & 1) === 0) return C[2];
    return null;
  });
  return cv.toTexture();
}

/** 여제의 명령 — 금빛 창에 세 갈래 관 모양 날끝, 둘레에 보랏빛 전하 */
function royal(r: number, seed: number): Texture {
  const C = cols(FX_RAMPS.royal);
  const V = cols(FX_RAMPS.violet);
  const rr = rand(6100 + r + seed * 23);
  const len = r * 6.5;
  const head = len / 2 - r * 0.2;
  const cv = new Canvas(-len / 2, len / 2 + r * 0.6, -r * 1.3, r * 1.3, 3 + seed);
  // 보랏빛 전하
  for (let k = 0; k < 4; k++) {
    const x = -len / 2 + rr() * len * 0.8;
    const side = rr() < 0.5 ? -1 : 1;
    cv.path(jagged(x, side * r * 0.3, x + r * 1.1, side * r * (0.8 + rr() * 0.4), 2, 2, rr), V[2], 1);
  }
  // 창 몸통
  for (let x = -len / 2; x <= head; x++) {
    const u = (x + len / 2) / (head + len / 2);
    const w = r * (0.1 + 0.28 * u) + (rr() < 0.3 ? 1 : 0);
    for (let y = -Math.ceil(w); y <= Math.ceil(w); y++) {
      const k = Math.abs(y) / Math.max(0.6, w);
      if (k > 1) continue;
      cv.put(x, y, k < 0.35 ? C[0] : k < 0.7 ? C[1] : k < 0.9 ? C[2] : C[3]);
    }
  }
  // 세 갈래 관
  for (const a of [-0.55, 0, 0.55]) {
    const l = a === 0 ? r * 1.0 : r * 0.7;
    const bx = head - r * 0.3;
    for (let s = 0; s <= l; s += 0.5) {
      const w = 2.2 * (1 - s / l);
      for (let o = -w; o <= w; o += 0.5) {
        const x = bx + Math.cos(a) * s - Math.sin(a) * o;
        const y = Math.sin(a) * s + Math.cos(a) * o;
        cv.put(x, y, s > l * 0.7 ? C[0] : Math.abs(o) > w - 0.6 ? C[3] : C[1]);
      }
    }
  }
  return cv.toTexture();
}

/** 가스 압착 — 검보랏빛 그을음 덩어리의 테두리가 타오른다(속이 밝은 플레임 스로우와 반대) */
function puff(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const S: RGB[] = [[138, 122, 134], [94, 81, 96], [58, 49, 64]];
  const rr = rand(7300 + r + seed * 41);
  const lobes = Array.from({ length: 7 }, () => 0.72 + rr() * 0.28);
  const cv = new Canvas(-r * 1.3, r * 1.3, -r * 1.3, r * 1.3, 1 + seed);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
    const f = a * lobes.length;
    const i0 = Math.floor(f) % lobes.length;
    const i1 = (i0 + 1) % lobes.length;
    const t = f - Math.floor(f);
    const lim = r * (lobes[i0] * (1 - t) + lobes[i1] * t);
    if (d > lim) return null;
    const k = d / lim;
    if (k > 0.9) return C[3];
    if (k > 0.66) return speck(x, y, 0.3, seed) ? C[1] : C[2];
    return speck(x, y, 0.4, seed + 7) ? S[0] : k < 0.3 ? S[2] : S[1];
  });
  for (let i = 0; i < 4; i++) {
    const a = rr() * Math.PI * 2;
    const d = r * (1.05 + rr() * 0.25);
    cv.put(Math.cos(a) * d, Math.sin(a) * d, C[1]);
  }
  return cv.toTexture();
}

/** 역풍 — 앞으로 볼록한 바람 초승달(충격파) */
function gustArc(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const Rc = r * 2.2;
  const cx = -r * 1.5;
  const cv = new Canvas(-r * 0.9, r * 0.9, -r * 1.7, r * 1.7, 1);
  for (let a = -0.7; a <= 0.7; a += 0.4 / Rc) {
    const u = 1 - Math.abs(a) / 0.7;
    const th = 0.6 + 1.6 * u;
    for (let k = 0; k <= th; k += 0.5) {
      const d = Rc - k;
      cv.put(cx + Math.cos(a) * d, Math.sin(a) * d, k < 0.8 ? C[0] : k < 1.5 ? C[1] : C[2]);
    }
    // 안쪽 잔결
    const d2 = Rc - 4 - seed;
    if (Math.abs(a) < 0.45 && ((Math.round(a * 40) + seed) & 3) < 2) cv.put(cx + Math.cos(a) * d2, Math.sin(a) * d2, C[2]);
  }
  return cv.toTexture();
}

/** 산들바람 — 바람에 실려 가는 나뭇잎 */
function leaf(r: number, _ramp: readonly string[]): Texture {
  const C = cols(FX_RAMPS.leaf);
  const cv = new Canvas(-r - 2, r, -r * 0.7, r * 0.7, 1);
  cv.paint((x, y) => {
    if (x < -r || x > r) return null;
    const half = r * 0.55 * Math.sin((Math.PI * (x + r)) / (2 * r));
    if (Math.abs(y) > half) return null;
    if (Math.abs(y) < 0.6) return C[1];
    return Math.abs(y) > half - 1 ? C[3] : y < 0 ? C[1] : C[2];
  });
  cv.put(-r - 1, 0, C[3]);
  cv.put(-r - 2, 1, C[3]);
  return cv.toTexture();
}

/** 기사 — 빙글빙글 도는 강철 낫(초승달 칼날) */
function blade(r: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-r, r, -r, r, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    if (d > r) return null;
    const d2 = Math.hypot(x - r * 0.42, y + r * 0.12);
    if (d2 < r * 0.78) return null;
    if (d > r - 1.1) return C[0];
    if (d2 < r * 0.78 + 1.2) return C[3];
    return d > r - 2.5 ? C[2] : C[3];
  });
  return cv.toTexture();
}

// ═══════════════════════════════════════════════════════════
//  궤도 (orbit) — 도는 물체 한 개
// ═══════════════════════════════════════════════════════════

/** 돌아가도 방향을 유지해야 하는 모양(불꽃은 늘 위로 탄다)이면 true */
export function orbitKeepsUpright(fx: string): boolean {
  return fx === 'wisp' || fx === 'spirit';
}

export function orbitFrames(fx: string, size: number, ramp: readonly string[]): Texture[] {
  const r = Math.max(3, Math.round(size));
  if (fx === 'wisp') return [0, 1, 2, 3].map((s) => wisp(r, ramp, s));
  if (fx === 'spirit') return [0, 1, 2, 3].map((s) => spirit(r, ramp, s));
  const shape: BoltShape =
    fx === 'spark' || fx === 'gust' || fx === 'chunk' || fx === 'shard' || fx === 'ember' ? fx : 'orb';
  return [bolt(shape, r, [...ramp])];
}

/** 파이어 스피릿 — 눈이 달린 도깨비불. 끝이 일렁인다. */
function wisp(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const body = r * 0.62;
  const cv = new Canvas(-r, r, -r * 1.7, r * 0.8, 1);
  const sway = Math.sin((seed / 4) * Math.PI * 2) * r * 0.18;
  cv.paint((x, y) => {
    // 아래는 둥글고 위로 갈수록 가늘게 흔들리며 솟는다
    let w: number;
    if (y > body) return null;
    if (y >= 0) w = Math.sqrt(Math.max(0, body * body - y * y));
    else {
      const u = -y / (r * 1.6);
      if (u > 1) return null;
      w = body * Math.pow(1 - u, 0.8);
      x -= sway * u * u;
    }
    const k = Math.abs(x) / Math.max(0.5, w);
    if (k > 1) return null;
    const v = Math.hypot(x / body, (y - body * 0.2) / (body * 1.2));
    return k > 0.86 ? C[4] : v < 0.35 ? C[0] : v < 0.6 ? C[1] : v < 0.85 ? C[2] : C[3];
  });
  const e = Math.max(1, Math.round(r * 0.12));
  for (const sx of [-1, 1]) {
    for (let yy = 0; yy <= e; yy++) cv.put(sx * r * 0.25, -r * 0.05 + yy, C[4]);
  }
  return cv.toTexture();
}

/** 다중 소환 유지 — 둘레에 빛점이 도는 정령구 */
function spirit(r: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-r, r, -r, r, 1);
  const pulse = 0.5 + 0.08 * Math.sin((seed / 4) * Math.PI * 2);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    if (d <= r * pulse) return d < r * pulse * 0.55 ? C[0] : d < r * pulse * 0.85 ? C[1] : C[2];
    if (Math.abs(d - r * 0.92) < 0.55 && ((Math.round(Math.atan2(y, x) * 8) + seed) & 1) === 0) return C[2];
    return null;
  });
  for (let k = 0; k < 4; k++) {
    const a = (k / 4) * Math.PI * 2 + (seed / 4) * (Math.PI / 2);
    const x = Math.cos(a) * r * 0.92;
    const y = Math.sin(a) * r * 0.92;
    cv.put(x, y, C[0]);
    cv.put(x + 1, y, C[1]);
    cv.put(x - 1, y, C[1]);
    cv.put(x, y + 1, C[1]);
    cv.put(x, y - 1, C[1]);
  }
  return cv.toTexture();
}

// ═══════════════════════════════════════════════════════════
//  오라 (aura) — 쿨타임 한 바퀴 동안 프레임을 돈다
// ═══════════════════════════════════════════════════════════

export function auraFrames(fx: string, radius: number, ramp: readonly string[]): FxFrames {
  switch (fx) {
    case 'obsidian': return obsidian(radius);
    case 'stomp': return stomp(radius, ramp);
    case 'cells': return cells(radius, ramp);
    default: return ring(radius, [...ramp], 5);
  }
}

/** 옵시디언 — 검은 유리 조각들이 몸 둘레를 돈다 */
function obsidian(R: number): FxFrames {
  const C = cols(FX_RAMPS.obsidian);
  const n = 8;
  const count = clamp(Math.round(R / 9), 10, 26);
  const rnd = rand(1717 + Math.round(R));
  const shards = Array.from({ length: count }, () => ({ len: 13 + rnd() * 7, w: 5 + rnd() * 2.5, bob: rnd() * Math.PI * 2 }));
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / n;
    const cv = new Canvas(-R - 4, R + 4, -R - 4, R + 4, 1);
    // 경계를 알리는 점선
    for (let a = 0; a < Math.PI * 2; a += 5 / R) cv.put(Math.cos(a) * R, Math.sin(a) * R, C[2]);
    shards.forEach((s, i) => {
      // 한 바퀴(프레임 8장)에 조각 한 칸만큼 돈다 → 이어 붙이면 끊김 없이 계속 돈다
      const a = ((i + t) / count) * Math.PI * 2;
      const d = R * 0.84 + Math.sin(s.bob + t * Math.PI * 2) * 2;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      for (let u = -s.len / 2; u <= s.len / 2; u += 0.5) {
        const w = s.w * (1 - Math.abs(u) / (s.len / 2));
        for (let o = -w; o <= w; o += 0.5) {
          const c = Math.abs(o) > w - 0.8 ? C[4] : o < 0 ? C[1] : u > 0 ? C[2] : C[3];
          cv.put(ca * (d + u) - sa * o, sa * (d + u) + ca * o, c);
        }
      }
      cv.put(ca * (d + s.len * 0.2) + sa, sa * (d + s.len * 0.2) - ca, C[0]);
    });
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay };
}

/** 타이탄 — 발을 구를 때마다 땅이 갈라지고, 울퉁불퉁한 흙더미 고리가 돌을 튀기며 퍼진다 */
function stomp(R: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 6;
  const rnd = rand(3131 + Math.round(R));
  const lumps = Array.from({ length: 28 }, () => rnd());
  const cracks = Array.from({ length: 9 }, (_, i) => {
    const a = (i / 9) * Math.PI * 2 + rnd() * 0.5;
    return jagged(Math.cos(a) * R * 0.1, Math.sin(a) * R * 0.1, Math.cos(a) * R * 0.97, Math.sin(a) * R * 0.97, 6, 3, rnd);
  });
  const rubble = Array.from({ length: 26 }, () => ({ a: rnd() * Math.PI * 2, o: rnd() * 8, s: rnd() < 0.4 ? 2 : 1 }));
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const cv = new Canvas(-R - 8, R + 8, -R - 8, R + 8, 50 + f);
    const rm = R * (0.16 + 0.82 * t);
    const th = R * 0.12 * (1 - t) + 3;
    // 금 (고리가 지나간 곳까지만)
    for (const c of cracks) {
      const part = c.filter(([x, y]) => Math.hypot(x, y) <= rm);
      if (part.length < 2) continue;
      cv.path(part, C[4], 2);
      cv.path(part.map(([x, y]) => [x - 1, y - 1] as [number, number]), C[2], 1);
    }
    cv.drop = t > 0.6 ? (t - 0.6) * 1.6 : 0;
    cv.paint((x, y) => {
      const d = Math.hypot(x, y);
      const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
      const jag = (lumps[Math.floor(a * lumps.length) % lumps.length] - 0.5) * 8;
      const off = d - rm - jag;
      if (Math.abs(off) > th) return null;
      const k = Math.abs(off) / th;
      if (off < -th * 0.6) return C[4];
      return k < 0.3 ? C[1] : k < 0.7 ? C[2] : C[3];
    });
    // 앞머리에서 튀는 돌
    for (const r of rubble) {
      const d = rm + th + r.o * (0.5 + t);
      const x = Math.cos(r.a) * d;
      const y = Math.sin(r.a) * d;
      if (r.s === 2) {
        cv.disc(x, y, 1.5, C[4]);
        cv.put(x, y, C[1]);
      } else cv.put(x, y, C[2]);
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay };
}

/** 세포 역설 — 둘레를 메운 세포들이 부풀었다 오그라든다 */
function cells(R: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 6;
  const outer = clamp(Math.round((Math.PI * 2 * R * 0.84) / 28), 8, 36);
  const inner = Math.round(outer * 0.45);
  const rnd = rand(2323 + Math.round(R));
  const list = [
    ...Array.from({ length: outer }, (_, i) => ({ a: (i / outer) * Math.PI * 2, d: R * (0.8 + rnd() * 0.12), s: 7.5 + rnd() * 3, ph: rnd() * 6 })),
    ...Array.from({ length: inner }, (_, i) => ({ a: (i / inner) * Math.PI * 2 + 0.3, d: R * (0.45 + rnd() * 0.12), s: 5 + rnd() * 2, ph: rnd() * 6 })),
  ];
  const textures: Texture[] = [];
  let ax = 0;
  let ay = 0;
  for (let f = 0; f < n; f++) {
    const t = f / n;
    const cv = new Canvas(-R - 6, R + 6, -R - 6, R + 6, 1);
    for (const c of list) {
      const rr2 = c.s * (0.8 + 0.3 * Math.sin(c.ph + t * Math.PI * 2));
      const a = c.a + t * 0.12;
      const x = Math.cos(a) * c.d;
      const y = Math.sin(a) * c.d;
      for (let dy = -Math.ceil(rr2); dy <= Math.ceil(rr2); dy++) {
        for (let dx = -Math.ceil(rr2); dx <= Math.ceil(rr2); dx++) {
          const d = Math.hypot(dx, dy);
          if (d > rr2) continue;
          if (d > rr2 - 1) cv.put(x + dx, y + dy, C[3]);
          else if (d > rr2 - 2.2) cv.put(x + dx, y + dy, C[2]);
          else if (speck(Math.round(x + dx), Math.round(y + dy), 0.3, 5)) cv.put(x + dx, y + dy, C[1]);
        }
      }
      cv.disc(x + 1, y - 1, 1.3, C[0]);
    }
    textures.push(cv.toTexture());
    ax = cv.anchorX;
    ay = cv.anchorY;
  }
  return { textures, anchorX: ax, anchorY: ay };
}

// ═══════════════════════════════════════════════════════════
//  장판 (field)
// ═══════════════════════════════════════════════════════════

export function fieldFrames(fx: string, radius: number, ramp: readonly string[]): Texture[] {
  const R = Math.max(6, Math.round(radius));
  switch (fx) {
    case 'snow': return [0, 1, 2].map((s) => snow(R, ramp, s));
    case 'fog': return [0, 1].map((s) => fog(R, s));
    case 'pillar': return [pillars(R, ramp)];
    case 'snowflake': return [snowflake(R, ramp)];
    case 'sigil': return [sigil(R)];
    case 'cinder': return [0, 1, 2].map((s) => cinder(R, ramp, s));
    default: return [fieldTexture(R, [...ramp])];
  }
}

/** 잿불 자국 — 그을린 땅 위에 불씨가 깜빡인다 (회광반조·불길 도배·불바다). 프레임마다 불씨 자리가 바뀐다 */
function cinder(R: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const soot = cols(['#2a1208', '#3d1a0a']);
  const cv = new Canvas(-R, R, -R, R, 700 + seed);
  const rr = rand(7100 + R);
  const lobes = Array.from({ length: 9 }, () => 0.7 + rr() * 0.3);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
    const lim = R * lobes[Math.floor(a * lobes.length) % lobes.length];
    if (d > lim) return null;
    const k = d / lim;
    // 그을음은 체크무늬로 반만 — 비쳐 보이게
    if (k > 0.55 && ((x + y) & 1) === 0) return null;
    if (speck(x, y, 0.12 * (1 - k), 31 + seed * 17)) return k < 0.4 ? C[0] : C[1];
    if (speck(x, y, 0.1, 57 + seed * 13)) return C[2];
    return k < 0.5 ? soot[1] : soot[0];
  });
  return cv.toTexture();
}

/** 에워싸는 눈바람 — 테두리 없이 흩날리는 눈송이. 프레임마다 조금씩 흘러간다. */
function snow(R: number, ramp: readonly string[], seed: number): Texture {
  const C = cols(ramp);
  const rr = rand(9100 + R);
  const cv = new Canvas(-R, R, -R, R, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y) / R;
    if (d > 1) return null;
    return speck(x - seed * 2, y - seed * 3, 0.07 * (1 - d * d), 11) ? C[2] : null;
  });
  const flakes = Math.round((Math.PI * R * R) / 110);
  for (let i = 0; i < flakes; i++) {
    const a = rr() * Math.PI * 2;
    const d = Math.sqrt(rr()) * R * 0.95;
    const x = Math.cos(a) * d + seed * 2;
    const y = Math.sin(a) * d + seed * 3;
    if (Math.hypot(x, y) > R - 1) continue;
    const big = rr() < 0.35;
    cv.put(x, y, C[0]);
    if (big) {
      cv.put(x + 1, y, C[1]);
      cv.put(x - 1, y, C[1]);
      cv.put(x, y + 1, C[1]);
      cv.put(x, y - 1, C[1]);
    }
  }
  return cv.toTexture();
}

/** 자욱한 안개 — 뭉게뭉게한 구름. 가장자리가 흐릿하게 사라진다. */
function fog(R: number, seed: number): Texture {
  const C = cols(FX_RAMPS.fog);
  const rr = rand(7700 + R);
  const blobs = Array.from({ length: 8 }, () => {
    const a = rr() * Math.PI * 2;
    const d = rr() * R * 0.55;
    return { x: Math.cos(a) * d, y: Math.sin(a) * d, r: R * (0.3 + rr() * 0.28) };
  });
  const cv = new Canvas(-R, R, -R, R, 1);
  // 결과는 예전과 한 칸도 다르지 않다 — 어차피 버릴 칸(원 밖, 덩어리 네모 밖)은 거리를 재기 전에 거른다.
  // 칸마다 덩어리 여덟 개와 거리를 재느라 큰 안개 하나 굽기에 0.1초 넘게 걸렸다
  cv.paint((x, y) => {
    if (Math.hypot(x, y) > R) return null;
    let dens = 0;
    for (const b of blobs) {
      const dx = x - b.x - seed * 2;
      const dy = y - b.y + seed;
      if (dx >= b.r || dx <= -b.r || dy >= b.r || dy <= -b.r) continue;
      const q = Math.hypot(dx, dy) / b.r;
      if (q < 1) dens = Math.max(dens, 1 - q * q);
    }
    if (dens <= 0) return null;
    if (speck(x + seed * 3, y, dens * 0.32, 21)) return dens > 0.7 ? C[1] : C[2];
    if (speck(x, y + seed * 2, dens * 0.22, 22)) return C[3];
    return null;
  });
  return cv.toTexture();
}

/** 돌기둥 — 금 간 땅 위로 육각 돌기둥 몇 개가 솟아 있다 */
function pillars(R: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const rr = rand(4747 + R);
  const cv = new Canvas(-R, R, -R - 26, R, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y) / R;
    if (d > 1) return null;
    if (d > 0.92) return speck(x, y, 0.65, 31) ? C[3] : null;
    return speck(x, y, 0.06, 32) ? C[3] : null;
  });
  for (let j = 0; j < 5; j++) {
    const a = rr() * Math.PI * 2;
    cv.path(jagged(0, 0, Math.cos(a) * R * 0.9, Math.sin(a) * R * 0.9, 3, 3, rr), C[4], 1);
  }
  const count = clamp(Math.round(R / 24), 3, 6);
  const cols2: Array<{ x: number; y: number; w: number; h: number }> = [];
  for (let i = 0; i < count; i++) {
    const a = (i / count) * Math.PI * 2 + rr() * 0.8;
    const d = (i === 0 ? 0 : 0.35 + rr() * 0.3) * R;
    cols2.push({ x: Math.cos(a) * d, y: Math.sin(a) * d + 6, w: 8 + rr() * 4, h: 13 + rr() * 11 });
  }
  cols2.sort((p, q) => p.y - q.y);
  for (const p of cols2) {
    // 그림자
    for (let dx = -p.w - 1; dx <= p.w + 1; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        if ((dx * dx) / ((p.w + 1) * (p.w + 1)) + (dy * dy) / 4 > 1) continue;
        cv.put(p.x + dx + 2, p.y + dy + 1, C[4]);
      }
    }
    // 옆면 (세로로 선 기둥)
    for (let dy = -p.h; dy <= 0; dy++) {
      for (let dx = -p.w; dx <= p.w; dx++) {
        const edge = Math.abs(dx) > p.w - 1;
        cv.put(p.x + dx, p.y + dy, edge ? C[4] : dx < -p.w * 0.3 ? C[2] : dx > p.w * 0.4 ? C[3] : C[2]);
      }
    }
    // 윗면 (육각)
    const top = p.y - p.h;
    for (let dy = -4; dy <= 4; dy++) {
      const half = p.w - Math.abs(dy) * 0.6;
      for (let dx = -Math.ceil(half); dx <= Math.ceil(half); dx++) {
        if (Math.abs(dx) > half) continue;
        const edge = Math.abs(dx) > half - 1 || Math.abs(dy) === 4;
        cv.put(p.x + dx, top + dy, edge ? C[3] : dx + dy < -2 ? C[0] : C[1]);
      }
    }
    // 옆면 금
    cv.line(p.x - p.w * 0.2, top + 5, p.x + p.w * 0.1, p.y - 2, C[3], 1);
  }
  return cv.toTexture();
}

/** 풍화설영 — 바닥 가득 찍힌 여섯 갈래 눈꽃 결정 (천천히 돈다) */
function snowflake(R: number, ramp: readonly string[]): Texture {
  const C = cols(ramp);
  const cv = new Canvas(-R, R, -R, R, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y) / R;
    if (d > 1) return null;
    if (d > 0.96) return ((x + y) & 1) === 0 ? C[2] : null;
    return speck(x, y, 0.05, 41) ? C[3] : null;
  });
  const L = R * 0.9;
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * Math.PI * 2;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    cv.line(0, 0, ca * L, sa * L, C[2], 3);
    cv.line(0, 0, ca * L, sa * L, C[0], 1);
    for (const [pos, len] of [[0.35, 0.28], [0.58, 0.22], [0.8, 0.14]] as const) {
      const bx = ca * L * pos;
      const by = sa * L * pos;
      for (const s of [-1, 1]) {
        const b = a + s * (Math.PI / 3);
        cv.line(bx, by, bx + Math.cos(b) * L * len, by + Math.sin(b) * L * len, C[1], 1);
      }
    }
  }
  for (let k = 0; k < 6; k++) {
    const a0 = (k / 6) * Math.PI * 2;
    const a1 = ((k + 1) / 6) * Math.PI * 2;
    const r0 = R * 0.12;
    cv.line(Math.cos(a0) * r0, Math.sin(a0) * r0, Math.cos(a1) * r0, Math.sin(a1) * r0, C[0], 2);
  }
  return cv.toTexture();
}

/** 메자이 — 금빛 이중 원에 육망성과 눈 모양 인장 (천천히 돈다) */
function sigil(R: number): Texture {
  const C = cols(FX_RAMPS.gold);
  const cv = new Canvas(-R, R, -R, R, 1);
  cv.paint((x, y) => {
    const d = Math.hypot(x, y);
    if (d > R) return null;
    if (Math.abs(d - (R - 1.5)) < 0.8) return C[1];
    if (Math.abs(d - (R - 6)) < 0.6) return C[2];
    // 두 원 사이 눈금
    if (d > R - 5.5 && d < R - 2) {
      const a = Math.atan2(y, x);
      const k = ((a / (Math.PI * 2)) * 24 + 24) % 1;
      if (k < 0.12) return C[0];
    }
    return speck(x, y, 0.04, 51) ? C[3] : null;
  });
  const T = R - 7;
  // 안쪽 원
  for (let a = 0; a < Math.PI * 2; a += 0.6 / T) cv.put(Math.cos(a) * T * 0.5, Math.sin(a) * T * 0.5, C[2]);
  for (const rot of [-Math.PI / 2, Math.PI / 2]) {
    const pts: Array<[number, number]> = [0, 1, 2, 3].map((i) => {
      const a = rot + (i / 3) * Math.PI * 2;
      return [Math.cos(a) * T, Math.sin(a) * T];
    });
    cv.path(pts, C[3], 3);
    cv.path(pts, C[1], 1);
  }
  // 한가운데 눈
  const e = Math.max(4, R * 0.12);
  for (let x = -e * 1.6; x <= e * 1.6; x++) {
    const hgt = e * Math.sqrt(Math.max(0, 1 - (x / (e * 1.6)) ** 2));
    cv.put(x, -hgt, C[0]);
    cv.put(x, hgt, C[0]);
  }
  cv.disc(0, 0, e * 0.45, C[1]);
  cv.put(0, 0, C[4]);
  return cv.toTexture();
}

// ═══════════════════════════════════════════════════════════
//  폭발 (burst)
// ═══════════════════════════════════════════════════════════

export function burstFrames(style: BurstStyle, radius: number, ramp: readonly string[]): FxFrames {
  const R = Math.max(8, radius);
  switch (style) {
    case 'fire': return fireBurst(R);
    case 'air': return airBurst(R, ramp);
    case 'spark': return sparkBurst(R, ramp);
    case 'claw': return clawBurst(R, ramp);
    case 'ember': return emberBurst(R);
    default: return ring(R, [...ramp], 5);
  }
}

/** 플레임 스로우가 맞은 자리 — 작고 붉은 불꽃이 확 퍼졌다가 검붉은 연기와 불똥으로 흩어진다 */
function emberBurst(R: number): FxFrames {
  // 흰 심지 → 주황 → 빨강 → 진홍 → 검붉은 연기. 파이어볼(주황 불덩이)보다 붉고 작다.
  const C = cols(['#fff1d0', '#ffb347', '#ff3b24', '#c0102a', '#4a0610']);
  const n = 5;
  const rnd = rand(2323 + Math.round(R));
  const lobes = Array.from({ length: 10 }, () => 0.72 + rnd() * 0.38);
  const sparks = Array.from({ length: 9 }, () => ({ a: rnd() * Math.PI * 2, v: 0.75 + rnd() * 0.6 }));
  const textures: Texture[] = [];
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const size = R * (0.4 + 0.6 * Math.sqrt(t));
    const hollow = Math.max(0, (t - 0.3) / 0.7);
    const cv = new Canvas(-R * 1.3, R * 1.3, -R * 1.3, R * 1.3, 60 + f);
    cv.drop = t > 0.5 ? (t - 0.5) * 1.3 : 0;
    cv.paint((x, y) => {
      const d = Math.hypot(x, y);
      const a = (Math.atan2(y, x) + Math.PI) / (Math.PI * 2);
      const lim = size * lobes[Math.floor(a * lobes.length) % lobes.length];
      if (d > lim) return null;
      const k = d / lim;
      if (k < hollow * 0.8) return null;
      const kk = (k - hollow * 0.8) / Math.max(0.2, 1 - hollow * 0.8);
      if (t < 0.3 && kk < 0.3) return C[0];
      return kk > 0.86 ? C[4] : kk > 0.62 ? C[3] : kk > 0.34 ? C[2] : C[1];
    });
    cv.drop = 0;
    for (const s of sparks) {
      const r = R * (0.55 + 0.75 * t) * s.v;
      cv.put(Math.cos(s.a) * r, Math.sin(s.a) * r, t < 0.6 ? C[1] : C[2]);
    }
    textures.push(cv.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5, fps: 24 };
}

/** 파이어볼이 터진다 — 속이 꽉 찬 불덩이가 부풀었다 불티로 흩어진다 */
function fireBurst(R: number): FxFrames {
  const C = cols(FX_RAMPS.flame);
  const n = 6;
  const rnd = rand(1111 + Math.round(R));
  const lobes = Array.from({ length: 14 }, () => 0.8 + rnd() * 0.3);
  const maxLobe = Math.max(...lobes);
  const textures: Texture[] = [];
  // 여섯 장면이 같은 크기의 판에 그린다 — 칸마다 거리·각도(hypot·atan2)를 장면마다 다시 구하던 것을 한 번만 구한다.
  // 그림은 예전과 한 칸도 다르지 않다(같은 식, 같은 순서). 큰 불폭발 한 번 굽기가 0.1초 넘게 걸려 판 도중 멈칫했다
  let polar: { d: Float64Array; a: Float64Array } | null = null;
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const size = R * (0.45 + 0.6 * Math.sqrt(t));
    const hollow = Math.max(0, (t - 0.35) / 0.65);
    const reach = size * maxLobe;
    const cv = new Canvas(-R * 1.1, R * 1.1, -R * 1.1, R * 1.1, 20 + f);
    cv.drop = t > 0.6 ? (t - 0.6) * 1.6 : 0;
    polar ??= cv.polar();
    const D = polar.d;
    const A = polar.a;
    cv.paintIdx((i) => {
      const d = D[i];
      if (d > reach) return null; // 가장 긴 꽃잎보다 멀면 볼 것도 없다
      const lim = size * lobes[Math.floor(A[i] * lobes.length) % lobes.length];
      if (d > lim) return null;
      const k = d / lim;
      if (k < hollow * 0.85) return null;
      const kk = (k - hollow * 0.85) / Math.max(0.15, 1 - hollow * 0.85);
      return kk > 0.88 ? C[4] : kk > 0.7 ? C[3] : kk > 0.45 ? C[2] : t < 0.5 && kk < 0.25 ? C[0] : C[1];
    });
    textures.push(cv.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5, fps: 22 };
}


/** 압축탄이 터진다 — 얇은 충격파 두 겹과 바깥으로 뻗는 바람 줄 */
function airBurst(R: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 5;
  const textures: Texture[] = [];
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const cv = new Canvas(-R - 2, R + 2, -R - 2, R + 2, 1);
    for (const [scale, c] of [[1, C[0]], [0.7, C[2]]] as const) {
      const rr2 = R * (0.3 + 0.7 * t) * scale;
      for (let a = 0; a < Math.PI * 2; a += 0.5 / Math.max(rr2, 1)) {
        if (t > 0.5 && ((Math.round(a * rr2) & 3) === 0)) continue;
        cv.put(Math.cos(a) * rr2, Math.sin(a) * rr2, c);
      }
    }
    for (let k = 0; k < 10; k++) {
      const a = (k / 10) * Math.PI * 2 + 0.2;
      const r0 = R * (0.15 + 0.6 * t);
      const r1 = r0 + R * 0.18;
      cv.line(Math.cos(a) * r0, Math.sin(a) * r0, Math.cos(a) * r1, Math.sin(a) * r1, C[1], 1);
    }
    textures.push(cv.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5, fps: 24 };
}

/** 전류가 튄 자리 — 가시 돋친 전기 별 */
function sparkBurst(R: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 4;
  const textures: Texture[] = [];
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const rr = rand(4040 + f * 7 + Math.round(R));
    const cv = new Canvas(-R - 2, R + 2, -R - 2, R + 2, 30 + f);
    cv.drop = t * 0.6;
    for (let k = 0; k < 7; k++) {
      const a = rr() * Math.PI * 2;
      const l = R * (0.5 + rr() * 0.5) * (0.6 + 0.4 * t);
      const pts = jagged(0, 0, Math.cos(a) * l, Math.sin(a) * l, 3, 3, rr);
      cv.path(pts, C[2], 2);
      cv.path(pts, C[0], 1);
    }
    cv.drop = 0;
    if (t < 0.5) cv.disc(0, 0, R * 0.18, C[0]);
    textures.push(cv.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5, fps: 24 };
}

/** 사냥 준비 착지 — 분홍 충격 고리 위에 세 줄 발톱 자국 (얼음의 정령왕은 얼음 램프로 할퀸다) */
function clawBurst(R: number, ramp: readonly string[]): FxFrames {
  const C = cols(ramp);
  const n = 5;
  const textures: Texture[] = [];
  for (let f = 0; f < n; f++) {
    const t = f / (n - 1);
    const cv = new Canvas(-R - 3, R + 3, -R - 3, R + 3, 70 + f);
    const rm = R * (0.25 + 0.75 * t);
    const th = R * (0.2 - 0.13 * t);
    cv.drop = Math.max(0, (t - 0.4) / 0.6) * 0.7;
    cv.paint((x, y) => {
      const off = Math.abs(Math.hypot(x, y) - rm);
      if (off > th) return null;
      return pick(C, off / th);
    });
    cv.drop = Math.max(0, (t - 0.55) / 0.45) * 0.8;
    const L = R * 0.8 * Math.min(1, 0.45 + t * 1.6);
    const W = Math.max(3, R * 0.06);
    for (let k = -1; k <= 1; k++) {
      const ox = k * R * 0.22;
      // 양 끝이 가는 발톱 자국
      for (let u = 0; u <= 1; u += 0.5 / L) {
        const x = ox - L * 0.5 + L * u;
        const y = -L * 0.5 + L * u;
        const w = W * Math.sin(Math.PI * u);
        cv.disc(x, y, w, C[3]);
        cv.disc(x, y, w * 0.55, C[1]);
        if (w > W * 0.5) cv.put(x, y, C[0]);
      }
    }
    textures.push(cv.toTexture());
  }
  return { textures, anchorX: 0.5, anchorY: 0.5, fps: 22 };
}

// ═══════════════════════════════════════════════════════════
//  거대한 검 (기사) — +x 를 향하고 기준점은 시전자. 칼날은 몸에서 조금 떨어져 시작한다.
// ═══════════════════════════════════════════════════════════

export function giantSword(R: number): { texture: Texture; anchorX: number; anchorY: number } {
  const L = Math.max(80, Math.round(R));
  const hw = Math.max(8, Math.round(L * 0.08));      // 칼날 반폭 — 두툼해야 '거대한' 검으로 보인다
  const gx0 = L * 0.05;                               // 자루 끝 (폼멜)
  const gx1 = L * 0.17;                               // 자루 → 코등이
  const bx0 = L * 0.2;                                // 칼날 시작
  const tipStart = L * 0.86;                          // 여기서부터 칼끝으로 좁아진다
  const guardH = Math.max(hw + 6, Math.round(L * 0.13));
  const blade: RGB[] = [[255, 255, 255], [223, 232, 245], [176, 190, 212], [110, 122, 148], [48, 54, 74]];
  const glow: RGB = [150, 210, 255];
  const gold: RGB[] = [[255, 243, 176], [255, 211, 107], [217, 160, 40], [140, 96, 16]];
  const grip: RGB[] = [[150, 100, 60], [100, 64, 36], [60, 36, 20]];
  const cv = new Canvas(-2, L + 2, -guardH - 2, guardH + 2, 1);

  // 칼날: 가장자리는 희게 빛나고, 가운데 홈(풀러)은 어둡다
  cv.paint((x, y) => {
    if (x < bx0 || x > L) return null;
    const w = x < tipStart ? hw : hw * Math.max(0, (L - x) / (L - tipStart));
    const ay = Math.abs(y);
    if (ay > w + 1.2) return null;
    if (ay > w) return glow;                                    // 영롱한 테두리
    if (ay > w - 1) return blade[4];                             // 외곽선
    if (ay > w - 2.2) return blade[0];                           // 날
    if (x < tipStart - 4 && ay < Math.max(1, hw * 0.28)) return blade[3];   // 홈
    return y < 0 ? blade[1] : blade[2];                          // 윗면이 밝다
  });
  // 코등이
  for (let x = Math.round(gx1); x <= Math.round(bx0); x++) {
    for (let y = -guardH; y <= guardH; y++) {
      const edge = x === Math.round(gx1) || x === Math.round(bx0) || Math.abs(y) === guardH;
      cv.put(x, y, edge ? gold[3] : y < 0 ? gold[1] : gold[2]);
    }
  }
  cv.disc((gx1 + bx0) / 2, 0, Math.max(2, hw * 0.45), gold[0]);
  // 자루: 감은 끈이 비스듬히
  const gh = Math.max(3, Math.round(hw * 0.55));
  for (let x = Math.round(gx0); x < Math.round(gx1); x++) {
    for (let y = -gh; y <= gh; y++) {
      const edge = Math.abs(y) === gh;
      cv.put(x, y, edge ? grip[2] : ((x + y) & 3) < 2 ? grip[0] : grip[1]);
    }
  }
  // 폼멜
  cv.disc(gx0, 0, gh + 1.5, gold[3]);
  cv.disc(gx0, 0, gh + 0.5, gold[1]);
  cv.put(gx0 - 1, -1, gold[0]);
  return { texture: cv.toTexture(), anchorX: cv.anchorX, anchorY: cv.anchorY };
}
